import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { userAtom } from "./UserAtom.ts";
import { formatAmount } from "../functions/helper.ts";
import { convertCurrency } from "../requests/budget.ts";

/**
 * Formats the engine's USD-base figures in the user's chosen currency.
 *
 * The cash-flow engine works entirely in USD; this fetches the USD -> currency
 * rate once and applies it. USD users (and the window before the rate loads)
 * format at par.
 */
export const useMoney = () => {
  const currentUser = useAtomValue(userAtom);
  const currency = currentUser?.currency || "USD";
  const [rate, setRate] = useState<number>(1);

  useEffect(() => {
    if (currency === "USD") {
      setRate(1);
      return;
    }

    let active = true;

    convertCurrency("USD", currency)
      .then((res) => {
        const next = res?.rates?.[currency];
        if (active && typeof next === "number" && next > 0) {
          setRate(next);
        }
      })
      .catch(() => {
        /* keep par rate on failure */
      });

    return () => {
      active = false;
    };
  }, [currency]);

  const money = (n: number) =>
    formatAmount((Number.isFinite(n) ? n : 0) * rate, currency);

  return { currency, money };
};
